import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Search, Plus, Bell } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ClientStatusPill } from "@/components/StatusPill";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";

const STATUSES = [
    { v: "all", l: "All statuses" },
    { v: "active", l: "Active" },
    { v: "pending_docs", l: "Pending docs" },
    { v: "submitted", l: "Submitted" },
    { v: "monitoring", l: "Monitoring" },
    { v: "past_due", l: "Past due" },
    { v: "cancelled", l: "Cancelled" },
    { v: "completed", l: "Completed" },
    { v: "inactive", l: "Inactive" },
];
const PROGRAMS = ["all", "unknown", "IDR", "IBR", "PAYE", "ICR", "Standard", "Other"];

export default function Clients() {
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [q, setQ] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");
    const [programFilter, setProgramFilter] = useState("all");
    const [hasAlertOnly, setHasAlertOnly] = useState(false);
    const [addOpen, setAddOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const [form, setForm] = useState({
        case_id: "", first_name: "", last_name: "", phone_raw: "",
        email: "", client_status: "active", program_type: "unknown",
    });

    const load = async () => {
        const params = { page, page_size: 25 };
        if (q) params.q = q;
        if (statusFilter !== "all") params.client_status = statusFilter;
        if (programFilter !== "all") params.program_type = programFilter;
        if (hasAlertOnly) params.has_active_alert = true;
        const { data } = await api.get("/clients", { params });
        setItems(data.items || []);
        setTotal(data.total || 0);
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [page, statusFilter, programFilter, hasAlertOnly]);

    const submitSearch = (e) => { e.preventDefault(); setPage(1); load(); };

    const addClient = async () => {
        setBusy(true);
        try {
            await api.post("/clients", { ...form, phone_e164: null });
            toast.success("Client created");
            setAddOpen(false);
            setForm({ case_id: "", first_name: "", last_name: "", phone_raw: "", email: "", client_status: "active", program_type: "unknown" });
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="space-y-5" data-testid="clients-page">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-display font-semibold text-gray-900">Clients</h1>
                    <p className="text-sm text-muted-foreground">Converted leads and active accounts.</p>
                </div>
                <Button onClick={() => setAddOpen(true)} data-testid="add-client-button">
                    <Plus className="h-4 w-4 mr-1" /> Add Client
                </Button>
            </div>

            <form onSubmit={submitSearch} className="flex flex-wrap items-center gap-2">
                <div className="relative flex-1 min-w-[200px]">
                    <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search A+ CaseFlow, case ID, name, phone, email…" className="pl-9" data-testid="clients-search-input" />
                </div>
                <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
                    <SelectTrigger className="w-[180px]" data-testid="clients-status-filter"><SelectValue /></SelectTrigger>
                    <SelectContent>{STATUSES.map(s => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}</SelectContent>
                </Select>
                <Select value={programFilter} onValueChange={(v) => { setProgramFilter(v); setPage(1); }}>
                    <SelectTrigger className="w-[150px]" data-testid="clients-program-filter"><SelectValue /></SelectTrigger>
                    <SelectContent>{PROGRAMS.map(p => <SelectItem key={p} value={p}>{p === "all" ? "All programs" : p}</SelectItem>)}</SelectContent>
                </Select>
                <Button
                    type="button"
                    variant={hasAlertOnly ? "default" : "outline"}
                    onClick={() => { setHasAlertOnly(v => !v); setPage(1); }}
                    data-testid="clients-alerts-only-toggle"
                >
                    <Bell className="h-4 w-4 mr-1" /> Active alerts only
                </Button>
            </form>

            <div className="bg-white border border-border rounded-xl overflow-hidden shadow-sm">
                <table className="w-full text-sm">
                    <thead className="bg-gray-50 text-left text-xs uppercase tracking-wider text-muted-foreground">
                        <tr>
                            <th className="px-4 py-2.5">A+ CaseFlow</th>
                            <th className="px-4 py-2.5">Case ID</th>
                            <th className="px-4 py-2.5">Client</th>
                            <th className="px-4 py-2.5">Phone</th>
                            <th className="px-4 py-2.5">Email</th>
                            <th className="px-4 py-2.5">Status</th>
                            <th className="px-4 py-2.5">Program</th>
                            <th className="px-4 py-2.5">Alerts</th>
                            <th className="px-4 py-2.5">Last Contacted</th>
                            <th className="px-4 py-2.5">Created</th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.length === 0 && (
                            <tr><td colSpan={10} className="px-4 py-10 text-center text-muted-foreground" data-testid="clients-empty-state">
                                <div className="font-medium text-gray-700">No clients found.</div>
                                <div className="text-xs mt-1">Convert a lead from the Leads page, or click <span className="font-medium">Add Client</span> above to create one manually.</div>
                            </td></tr>
                        )}
                        {items.map(c => (
                            <tr key={c.id} className="border-t border-border hover:bg-gray-50">
                                <td className="px-4 py-2.5 font-mono text-xs" data-testid={`client-acos-${c.id}`} data-acos-cell="">{c.acos_id != null ? c.acos_id : "—"}</td>
                                <td className="px-4 py-2.5 font-mono text-xs">{c.case_id || "—"}</td>
                                <td className="px-4 py-2.5">
                                    <Link to={`/clients/${c.id}`} className="text-gray-900 hover:text-primary font-medium" data-testid={`client-row-${c.id}`}>
                                        {c.client_name || `${c.first_name} ${c.last_name}`.trim() || "—"}
                                    </Link>
                                </td>
                                <td className="px-4 py-2.5">{c.phone_e164 || c.phone_raw || "—"}</td>
                                <td className="px-4 py-2.5">{c.email || "—"}</td>
                                <td className="px-4 py-2.5"><ClientStatusPill status={c.client_status || "active"} /></td>
                                <td className="px-4 py-2.5 text-xs">{c.program_type || "—"}</td>
                                <td className="px-4 py-2.5">
                                    {c.active_alerts_count > 0
                                        ? <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-md bg-rose-100 text-rose-700"><Bell className="h-3 w-3" />{c.active_alerts_count}</span>
                                        : <span className="text-xs text-muted-foreground">—</span>}
                                </td>
                                <td className="px-4 py-2.5 text-xs text-muted-foreground">{c.last_contacted_at ? new Date(c.last_contacted_at).toLocaleDateString() : "—"}</td>
                                <td className="px-4 py-2.5 text-xs text-muted-foreground">{c.created_at ? new Date(c.created_at).toLocaleDateString() : "—"}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <div className="flex items-center justify-between text-xs text-muted-foreground">
                <div>Total: {total}</div>
                <div className="flex gap-2">
                    <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(p => Math.max(1, p - 1))}>Prev</Button>
                    <Button size="sm" variant="outline" disabled={items.length < 25} onClick={() => setPage(p => p + 1)}>Next</Button>
                </div>
            </div>

            <Dialog open={addOpen} onOpenChange={setAddOpen}>
                <DialogContent className="sm:max-w-[460px]" data-testid="add-client-dialog">
                    <DialogHeader>
                        <DialogTitle>Add Client</DialogTitle>
                        <DialogDescription>Create a client manually. Most clients should be converted from a lead.</DialogDescription>
                    </DialogHeader>
                    <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1.5 col-span-2">
                            <label className="text-xs font-medium text-gray-700">Case ID</label>
                            <Input value={form.case_id} onChange={e => setForm({ ...form, case_id: e.target.value })} data-testid="new-client-case-id" />
                        </div>
                        <div className="space-y-1.5">
                            <label className="text-xs font-medium text-gray-700">First Name</label>
                            <Input value={form.first_name} onChange={e => setForm({ ...form, first_name: e.target.value })} data-testid="new-client-first-name" />
                        </div>
                        <div className="space-y-1.5">
                            <label className="text-xs font-medium text-gray-700">Last Name</label>
                            <Input value={form.last_name} onChange={e => setForm({ ...form, last_name: e.target.value })} data-testid="new-client-last-name" />
                        </div>
                        <div className="space-y-1.5">
                            <label className="text-xs font-medium text-gray-700">Phone</label>
                            <Input value={form.phone_raw} onChange={e => setForm({ ...form, phone_raw: e.target.value })} data-testid="new-client-phone" />
                        </div>
                        <div className="space-y-1.5">
                            <label className="text-xs font-medium text-gray-700">Email</label>
                            <Input value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} data-testid="new-client-email" />
                        </div>
                        <div className="space-y-1.5">
                            <label className="text-xs font-medium text-gray-700">Status</label>
                            <Select value={form.client_status} onValueChange={v => setForm({ ...form, client_status: v })}>
                                <SelectTrigger><SelectValue /></SelectTrigger>
                                <SelectContent>{STATUSES.filter(s => s.v !== "all").map(s => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}</SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-1.5">
                            <label className="text-xs font-medium text-gray-700">Program</label>
                            <Select value={form.program_type} onValueChange={v => setForm({ ...form, program_type: v })}>
                                <SelectTrigger><SelectValue /></SelectTrigger>
                                <SelectContent>{PROGRAMS.filter(p => p !== "all").map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                            </Select>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setAddOpen(false)} disabled={busy}>Cancel</Button>
                        <Button onClick={addClient} disabled={busy} data-testid="new-client-submit">{busy ? "Adding…" : "Add Client"}</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
